import { Component, EventEmitter, HostListener, OnDestroy, Output, TemplateRef, ViewChild } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { HistoryService } from "../../services/history.service";
import { Helper } from "src/app/core/helpers/helper";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { debounceTime, distinctUntilChanged, Subject, Subscription, switchMap } from "rxjs";
import { MapBoxService } from "../../services/mapbox.service";
import { GoogleMapsService } from "../../services/google-map.service";
import { CommonService } from "../../services/common.service";
import { AccessibilityService } from "../../services/accessibility.service";

@Component({
  selector: "app-booking-status-change-confirmation-modal",
  templateUrl: "./booking-status-change-confirmation-modal.component.html",
  styleUrl: "./booking-status-change-confirmation-modal.component.scss",
  standalone: false
})
export class BookingStatusChangeConfirmationModalComponent implements OnDestroy {
  private session = 0;
  private destroyed = false;
  selectedCompletedAddressMapSearchIndex: number = -1;

  isCancelBooking: boolean = false;

  bookingDetails: any;
  countryDetails: any;
  setCurrencySign: any;
  currencySign: any;
  decimalPointValue: any;
  cancellationCharge: any;

  completedAddressPredictions: Array<any> = [];
  completedAddressMapBoxSuggestions: Array<any> = [];
  cancelBookingReasonList: Array<any> = [];
  bookingDetaisAdditionalPrices: Array<any> = [];

  completeBookingDetailsForm!: FormGroup;

  private readonly completeAddressInputSubject: Subject<string> = new Subject<string>();
  private completeAddressInputSubscription!: Subscription;

  bookingStatusChangeConfirmationModalRef!: NgbModalRef;

  customPrices: Array<any> = [];
  selectedBusinessType: any;
  currentBookingBusinessType: number = this.helper.BusinessType.TAXI;

  @Output() changeBookingStatusEvent = new EventEmitter();

  @ViewChild("bookingStatusChangeConfirmationModal") bookingStatusChangeConfirmationModal!: TemplateRef<any>;

  constructor(
    public modalService: NgbModal,
    private readonly historyService: HistoryService,
    public helper: Helper,
    private readonly googleMapsApiService: GoogleMapsService,
    private readonly mapBoxService: MapBoxService,
    private readonly commonService: CommonService,
    private readonly accessibilityService: AccessibilityService
  ) {}

  /**
   * Opens the modal for booking status change confirmation.
   * Initializes form controls and fetches necessary data based on booking details.
   *
   * @param bookingDetails The details of the booking.
   * @param countryDetails The details of the country.
   * @param isCancelBooking Flag indicating if the booking is being canceled.
   */
  async openModal(bookingDetails: any, countryDetails: any, isCancelBooking: boolean, selectedBusinessType: any): Promise<void> {
    if (this.destroyed) return;
    this.closeModal();
    const session = this.session;
    this.selectedBusinessType = selectedBusinessType;
    this.bookingDetails = JSON.parse(JSON.stringify(bookingDetails));
    this.currentBookingBusinessType = this.bookingDetails.businessType;
    this.countryDetails = countryDetails;
    this.isCancelBooking = isCancelBooking;
    this.bookingDetaisAdditionalPrices = this.bookingDetails.bookingInvoice?.estimated?.additionalPrices ?? [];

    if (this.isCancelBooking) {
      await this.getCancellationReasonList();
    }
    if (session !== this.session || this.destroyed) return;

    this.initCompleteBookingDetailsForm();

    this.patchCompleteAddress();

    this.makeAdditionPriceFormControl();

    if (
      this.getNextBookingStatus(this.currentBookingBusinessType) === this.helper.BookingStatus.ARRIVED_AT_DESTINATION &&
      (this.currentBookingBusinessType === this.helper.BusinessType.TAXI || this.currentBookingBusinessType === this.helper.BusinessType.TAXI_METER || this.currentBookingBusinessType === this.helper.BusinessType.TOWING) &&
      !this.isCancelBooking
    ) {
      await this.getAdminVehicleAccessibilityList();
      if (session !== this.session || this.destroyed) return;
      if (selectedBusinessType !== this.helper.BusinessType.SERVICE) {
        this.initCompleteAddressAutoComplete();
      }
    }

    this.bookingStatusChangeConfirmationModalRef = this.modalService.open(this.bookingStatusChangeConfirmationModal, { centered: true, backdrop: "static" });
    this.bookingStatusChangeConfirmationModalRef.result.then(
      () => {},
      () => {}
    );
  }

  /**
   * Closes the modal.
   */
  closeModal(): void {
    this.session++;
    this.completeAddressInputSubscription?.unsubscribe();
    this.bookingStatusChangeConfirmationModalRef?.dismiss("Cross click");
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.closeModal();
  }

  /**
   * Fetches the list of cancellation reasons.
   *
   * @returns A promise that resolves when the cancellation reasons are fetched.
   */
  getCancellationReasonList(): Promise<void> {
    return new Promise((resolve) => {
      const parameter = { type: this.helper.UserType.DISPATCHER };
      this.commonService.getCancellationReasonList(parameter).subscribe((response: any) => {
        this.cancelBookingReasonList = response?.cancellationReasons?.length > 0 ? response.cancellationReasons : [];
        resolve();
      });
    });
  }

  /**
   * Initializes the form group for complete booking details.
   */
  initCompleteBookingDetailsForm(): void {
    this.completeBookingDetailsForm = new FormGroup({
      note: new FormControl<string | null>(null)
    });

    if (
      this.getNextBookingStatus(this.currentBookingBusinessType) === this.helper.BookingStatus.ARRIVED_AT_DESTINATION &&
      (this.currentBookingBusinessType === this.helper.BusinessType.TAXI || this.currentBookingBusinessType === this.helper.BusinessType.TAXI_METER || this.currentBookingBusinessType === this.helper.BusinessType.TOWING) &&
      !this.isCancelBooking
    ) {
      this.completeBookingDetailsForm.addControl("completeAddress", new FormControl<string | null>(null, [Validators.required]));
      this.completeBookingDetailsForm.addControl("stopAddress", new FormControl<any>(null, [Validators.required]));
    }

    if (this.isCancelBooking) {
      this.completeBookingDetailsForm.addControl("cancellationReason", new FormControl<string | null>(null, [Validators.required]));
      this.completeBookingDetailsForm.addControl("specificReason", new FormControl<string | null>(null));
    }
  }

  /**
   * Creates form controls for additional prices if any.
   */
  makeAdditionPriceFormControl(): void {
    if (this.bookingDetaisAdditionalPrices.length > 0) {
      this.completeBookingDetailsForm.addControl("prices", new FormArray([]));

      this.bookingDetaisAdditionalPrices.forEach((priceObj: any) => {
        this.prices.push(
          new FormGroup({
            price: new FormControl(priceObj.price, [Validators.required, this.helper.minValueValidator(0)])
          })
        );
      });
    }
  }

  /**
   * Fetches the cancellation charge for the booking.
   */
  getCancellationCharge(): void {
    this.historyService.bookingCancellationCharge(this.bookingDetails._id, { isShowErrorToast: false }).subscribe((response: any) => {
      this.cancellationCharge = response.total;
    });
  }

  /**
   * Gets the form array for prices.
   *
   * @returns The form array for prices.
   */
  get prices(): FormArray {
    return this.completeBookingDetailsForm.get("prices") as FormArray;
  }

  /**
   * Determines the next booking status based on the current status.
   *
   * @returns The next booking status.
   */

  getNextBookingStatus(bookingType: number = this.helper.BusinessType.TAXI): number {
    const status = this.bookingDetails?.status;

    if (status === this.helper.BookingStatus.ACCEPTED) {
      return this.getNextStatusForAccepted();
    } else if (status === this.helper.BookingStatus.SERVICE_COMPLETED) {
      return this.getNextStatusForServiceCompleted();
    } else if (status === this.helper.BookingStatus.IN_ROUTE) {
      return this.helper.BookingStatus.ARRIVED_AT_PICKUP;
    } else if (status === this.helper.BookingStatus.ARRIVED_AT_PICKUP) {
      return this.getNextStatusForArrivedAtPickup(bookingType);
    } else if (status === this.helper.BookingStatus.PICKED) {
      return this.helper.BookingStatus.STARTED;
    } else if (status === this.helper.BookingStatus.ARRIVED_NEAR_DESTINATION) {
      return this.helper.BookingStatus.ARRIVED_AT_DESTINATION;
    } else if (status === this.helper.BookingStatus.STARTED || status === this.helper.BookingStatus.ARRIVED_AT_STOP) {
      return this.getNextStatusForStartedOrArrivedAtStop(bookingType);
    }

    return 0;
  }

  private getNextStatusForAccepted(): number {
    if (
      this.bookingDetails.bookingType === this.helper.BookingType.ONLINE_SERVICE ||
      (this.bookingDetails.bookingType === this.helper.BookingType.ON_SITE_SERVICE &&
        !this.bookingDetails.bookingTags.includes(this.helper.BookingType[this.helper.BookingType.PICKUP_FROM_HOME]))
    ) {
      return this.helper.BookingStatus.STARTED;
    }
    return this.helper.BookingStatus.IN_ROUTE;
  }

  private getNextStatusForServiceCompleted(): number {
    if (this.bookingDetails.bookingTags.includes(this.helper.BookingType[this.helper.BookingType.DROP_AT_HOME])) {
      return this.helper.BookingStatus.IN_ROUTE;
    }
    return this.helper.BookingStatus.ARRIVED_AT_DESTINATION;
  }

  private getNextStatusForArrivedAtPickup(bookingType: number): number {
    if (this.bookingDetails.quickCommerce.status === this.helper.BookingStatus.SERVICE_COMPLETED) {
      return this.helper.BookingStatus.ARRIVED_AT_DESTINATION;
    } else if (bookingType === this.helper.BusinessType.DELIVERY || bookingType === this.helper.BusinessType.QUICK_COMMERCE) {
      return this.helper.BookingStatus.PICKED;
    } else if (
      this.bookingDetails.bookingType === this.helper.BookingType.ON_SITE_SERVICE &&
      this.bookingDetails.bookingTags.includes(this.helper.BookingType[this.helper.BookingType.PICKUP_FROM_HOME])
    ) {
      return this.helper.BookingStatus.PICKED;
    }
    return this.helper.BookingStatus.STARTED;
  }

  private getNextStatusForStartedOrArrivedAtStop(bookingType: number): number {
    if (
      this.bookingDetails.bookingType === this.helper.BookingType.ON_SITE_SERVICE &&
      (this.bookingDetails.bookingTags.includes(this.helper.BookingType[this.helper.BookingType.DROP_AT_HOME]) ||
        this.bookingDetails.bookingTags.includes(this.helper.BookingType[this.helper.BookingType.PICKUP_FROM_HOME]))
    ) {
      return this.helper.BookingStatus.SERVICE_COMPLETED;
    }
    return bookingType === this.helper.BusinessType.DELIVERY || bookingType === this.helper.BusinessType.QUICK_COMMERCE
      ? this.helper.BookingStatus.ARRIVED_NEAR_DESTINATION
      : this.helper.BookingStatus.ARRIVED_AT_DESTINATION;
  }

  /**
   * Gets the string representation of the current booking status.
   *
   * @returns The string representation of the current booking status.
   */

  getBookingStatusString(businessType: number = this.helper.BusinessType.TAXI): string {
    switch (this.bookingDetails?.status) {
      case this.helper.BookingStatus.ACCEPTED:
        return this.helper.translateService.instant("label-title.coming");

      case this.helper.BookingStatus.IN_ROUTE:
        return this.helper.translateService.instant("label-title.arrived-at-pickup");

      case this.helper.BookingStatus.ARRIVED_AT_PICKUP:
        return businessType === this.helper.BusinessType.DELIVERY || businessType === this.helper.BusinessType.QUICK_COMMERCE
          ? this.helper.translateService.instant("label-title.picked")
          : this.helper.translateService.instant("label-title.started");

      case this.helper.BookingStatus.PICKED:
        return this.helper.translateService.instant("label-title.started");

      case this.helper.BookingStatus.STARTED:
        return businessType === this.helper.BusinessType.DELIVERY || businessType === this.helper.BusinessType.QUICK_COMMERCE
          ? this.helper.translateService.instant("label-title.arrived-at-destination")
          : this.helper.translateService.instant("label-title.completed");

      case this.helper.BookingStatus.ARRIVED_AT_STOP:
      case this.helper.BookingStatus.ARRIVED_NEAR_DESTINATION:
        return this.helper.translateService.instant("label-title.completed");

      default:
        return "";
    }
  }

  /**
   * Gets the string representation for canceling a booking.
   *
   * @returns The string representation for canceling a booking.
   */
  getCancelBookingString(): void {
    return this.helper.translateService.instant("label-title.cancel");
  }

  /**
   * Initializes the autocomplete functionality for the complete address input.
   */
  initCompleteAddressAutoComplete(): void {
    const params = this.countryDetails?.code2 ? { region: this.countryDetails?.code2 } : {};

    if (this.completeAddressInputSubscription) {
      this.completeAddressInputSubscription.unsubscribe();
    }
    this.completeAddressInputSubscription = this.completeAddressInputSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((input) => {
          this.completedAddressPredictions = [];
          return this.googleMapsApiService.getPlacePredictions(input, params);
        })
      )
      .subscribe((predictions) => {
        this.completedAddressPredictions = predictions;
      });
  }

  /**
   * Handles changes in the complete address input field.
   *
   * @param event The input event.
   */
  onCompleteAddressInputChange(event: Event): void {
    if (this.helper.mapTypeGoogle()) {
      const inputElement = event.target as HTMLInputElement;
      const inputValue = inputElement.value.trim();

      this.completeAddressInputSubject.next(inputValue);
    } else if (this.helper.mapTypeMapBox()) {
      this.debouncedOnCompletedAddressSearchMapbox(event);
    }
  }

  /**
   * Debounced function for handling address search using MapBox.
   *
   * @param event The input event.
   */
  debouncedOnCompletedAddressSearchMapbox = this.helper.debounce(this.onSearch.bind(this), 500);

  /**
   * Searches for address suggestions using MapBox.
   *
   * @param event The input event.
   */
  onSearch(event: any): void {
    const query = event.target.value;
    if (query.length > 2) {
      const code: string = this.countryDetails.code ?? "";
      this.mapBoxService.search(query, code).subscribe((response: any) => {
        this.completedAddressMapBoxSuggestions = response.features;
      });
    } else {
      this.completedAddressMapBoxSuggestions = [];
    }
  }

  /**
   * Checks if a completed address is selected.
   *
   * @param index The index of the address.
   * @returns True if the address is selected, false otherwise.
   */
  isCompletedAddressSelected(index: number): boolean {
    return this.selectedCompletedAddressMapSearchIndex === index;
  }

  /**
   * Handles the selection of a prediction from Google Maps.
   *
   * @param prediction The selected prediction.
   */
  async onPredictionSelect(prediction: any): Promise<void> {
    this.completedAddressPredictions = [];
    const place = await this.googleMapsApiService.fetchFields(prediction);
    this.completeBookingDetailsForm.patchValue({
      completeAddress: place.formatted_address,
      stopAddress: this.extractAddressDetailsUsingGoogle(place, prediction)
    });
  }

  /**
   * Extracts address details from a Google Maps place object.
   *
   * @param place The place object.
   * @param prediction The prediction object.
   * @returns The extracted address details.
   */
  extractAddressDetailsUsingGoogle(place: any, prediction: any): any {
    const getAddressComponent = (types: string[]): any => place.address_components.find((component: any) => types.some((type) => component.types.includes(type)));

    const cityComponent = getAddressComponent(["locality", "administrative_area_level_3"]);
    const city = cityComponent?.longText ?? "";

    const countryComponent = getAddressComponent(["country"]);
    const country = countryComponent?.longText ?? "";
    const countryCode = countryComponent?.shortText ?? "";

    const postalCodeComponent = getAddressComponent(["postal_code"]);
    const postalCode = postalCodeComponent?.longText ?? "";

    return {
      address: place.formatted_address,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      city,
      countryCode,
      country,
      postalCode,
      note: "",
      placeId: prediction.place_id
    };
  }

  /**
   * Handles keyboard events for address input fields.
   *
   * @param event The keyboard event.
   */
  @HostListener("document:keydown", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.helper.mapTypeGoogle()) {
      this.handleGoogleMapKeyboardEvent(event);
    } else if (this.helper.mapTypeMapBox()) {
      this.handleMapBoxKeyboardEvent(event);
    }
  }

  /**
   * Handles keyboard events for Google Maps address input fields.
   *
   * @param event The keyboard event.
   */
  handleGoogleMapKeyboardEvent(event: KeyboardEvent): void {
    if (this.completedAddressPredictions.length > 0) {
      if (event.key === "ArrowDown") {
        this.selectedCompletedAddressMapSearchIndex = (this.selectedCompletedAddressMapSearchIndex + 1) % this.completedAddressPredictions.length;
      } else if (event.key === "ArrowUp") {
        this.selectedCompletedAddressMapSearchIndex =
          (this.selectedCompletedAddressMapSearchIndex - 1 + this.completedAddressPredictions.length) % this.completedAddressPredictions.length;
      } else if (
        event.key === "Enter" &&
        this.selectedCompletedAddressMapSearchIndex >= 0 &&
        this.selectedCompletedAddressMapSearchIndex < this.completedAddressPredictions.length
      ) {
        this.onPredictionSelect(this.completedAddressPredictions[this.selectedCompletedAddressMapSearchIndex]);
        this.selectedCompletedAddressMapSearchIndex = -1;
      }
    }
  }

  /**
   * Handles keyboard events for MapBox address input fields.
   *
   * @param event The keyboard event.
   */
  handleMapBoxKeyboardEvent(event: KeyboardEvent): void {
    if (this.completedAddressMapBoxSuggestions.length > 0) {
      if (event.key === "ArrowDown") {
        this.selectedCompletedAddressMapSearchIndex = (this.selectedCompletedAddressMapSearchIndex + 1) % this.completedAddressMapBoxSuggestions.length;
      } else if (event.key === "ArrowUp") {
        this.selectedCompletedAddressMapSearchIndex =
          (this.selectedCompletedAddressMapSearchIndex - 1 + this.completedAddressMapBoxSuggestions.length) % this.completedAddressMapBoxSuggestions.length;
      } else if (
        event.key === "Enter" &&
        this.selectedCompletedAddressMapSearchIndex >= 0 &&
        this.selectedCompletedAddressMapSearchIndex < this.completedAddressMapBoxSuggestions.length
      ) {
        this.onSelect(this.completedAddressMapBoxSuggestions[this.selectedCompletedAddressMapSearchIndex]);
        this.selectedCompletedAddressMapSearchIndex = -1;
      }
    }
  }

  /**
   * Handles the selection of a suggestion from MapBox.
   *
   * @param suggestion The selected suggestion.
   */
  onSelect(suggestion: any): void {
    this.completeBookingDetailsForm.patchValue({
      completeAddress: suggestion.place_name,
      stopAddress: this.extractAddressDetailsFromMapBox(suggestion)
    });

    this.completedAddressMapBoxSuggestions = [];
  }

  /**
   * Extracts address details from a MapBox suggestion object.
   *
   * @param suggestion The suggestion object.
   * @returns The extracted address details.
   */
  extractAddressDetailsFromMapBox(suggestion: any): any {
    const getContextItem = (type: string): any => suggestion.context.find((item: any) => item.id.startsWith(type));

    const cityContext = getContextItem("place");
    const city = cityContext?.text ?? "";

    const countryContext = getContextItem("country");
    const country = countryContext?.text ?? "";
    const countryCode = countryContext?.short_code.toUpperCase() ?? "";

    const postalCodeContext = getContextItem("postcode");
    const postalCode = postalCodeContext?.text ?? "";

    return {
      address: suggestion.place_name,
      latitude: suggestion.geometry.coordinates[1],
      longitude: suggestion.geometry.coordinates[0],
      city,
      countryCode,
      country,
      postalCode,
      note: "",
      placeId: suggestion.id
    };
  }

  /**
   * Starts the button loader.
   */
  startBtnLoader(): void {
    if (this.helper.showBtnLoader[1] === true) {
      return;
    }

    this.helper.showBtnLoader[1] = true;
  }

  /**
   * Updates the booking status based on the current status and form values.
   */
  updateBookingStatus(): void {
    const parameters: any = {
      bookingId: this.bookingDetails._id,
      driverId: this.bookingDetails.confirmedDriver?.id,
      status: this.getNextBookingStatus(this.currentBookingBusinessType),
      businessType: this.bookingDetails.businessType
    };

    if (this.getNextBookingStatus() === this.helper.BookingStatus.ARRIVED_AT_DESTINATION && !this.isCancelBooking) {
      if (this.completeBookingDetailsForm.invalid && this.selectedBusinessType !== this.helper.BusinessType.SERVICE) {
        console.warn("Invalid form");
        this.completeBookingDetailsForm.markAllAsTouched();
        return;
      }

      this.handleAdditionalPrices(parameters);

      parameters["stopAddress"] = this.completeBookingDetailsForm.value.stopAddress;
    }

    parameters["note"] = this.completeBookingDetailsForm.value.note ?? "";

    this.startBtnLoader();

    this.historyService.changeBookingStatus(this.bookingDetails._id, parameters).subscribe((response: any) => {
      this.helper.showBtnLoader[1] = false;
      if (response) {
        this.closeModal();
        setTimeout(() => {
          this.bookingDetails.status = this.getNextBookingStatus(this.currentBookingBusinessType);
          this.changeBookingStatusEvent.emit(this.bookingDetails);
        }, 500);
      }
    });
  }

  /**
   * Handles additional prices for the booking.
   *
   * @param parameters The parameters object to be updated.
   */
  handleAdditionalPrices(parameters: any): void {
    if (this.bookingDetaisAdditionalPrices?.length > 0) {
      parameters["additionalPrices"] = this.getUpdatedAdditionPrices();
    }
  }

  /**
   * Gets the updated additional prices.
   *
   * @returns The updated additional prices.
   */
  getUpdatedAdditionPrices(): Array<any> {
    this.bookingDetaisAdditionalPrices = this.completeBookingDetailsForm.value.prices.map((priceObj: any, index: any) => {
      return {
        ...this.bookingDetaisAdditionalPrices[index],
        price: priceObj.price
      };
    });

    return this.bookingDetaisAdditionalPrices;
  }

  /**
   * Cancels the booking.
   */
  cancelBooking(): void {
    this.completeBookingDetailsForm.patchValue({
      specificReason: this.completeBookingDetailsForm.value.specificReason?.toString()?.trim()
    });

    this.setSpecificReasonValidators();

    if (this.completeBookingDetailsForm.invalid) {
      this.completeBookingDetailsForm.markAllAsTouched();
      return;
    }

    const parameters: any = {
      note: this.completeBookingDetailsForm.value.note ?? ""
    };

    if (this.completeBookingDetailsForm.value.cancellationReason === "Other") {
      parameters.cancellationReason = this.completeBookingDetailsForm.value.specificReason;
    } else {
      parameters.cancellationReason = this.completeBookingDetailsForm.value.cancellationReason;
    }

    this.startBtnLoader();

    this.historyService.cancelBooking(this.bookingDetails._id, parameters).subscribe((response: any) => {
      this.helper.showBtnLoader[1] = false;
      if (response) {
        this.closeModal();
        setTimeout(() => {
          this.bookingDetails.status = this.helper.BookingStatus.CANCELLED;
          this.changeBookingStatusEvent.emit(this.bookingDetails);
        }, 500);
      }
    });
  }

  /**
   * Sets and emits the updated booking details.
   */
  setAndEmitBookingDetails(): void {
    setTimeout(() => {
      this.bookingDetails.status = this.getNextBookingStatus();
      this.changeBookingStatusEvent.emit(this.bookingDetails);
    }, 500);
  }

  /**
   * Handles changes in the cancellation reason.
   */
  onReasonChange(): void {
    if (this.completeBookingDetailsForm.value.cancellationReason === "other") {
      this.completeBookingDetailsForm.addControl("reason", new FormControl<string | null>(null, [Validators.required]));
    } else {
      this.completeBookingDetailsForm.removeControl("reason");
    }
  }

  patchCompleteAddress(): void {
    const destinationAddresses = JSON.parse(JSON.stringify(this.bookingDetails?.destinationAddresses.slice(-1)));
    const address: any = destinationAddresses.length > 0 ? destinationAddresses[0] : this.bookingDetails.address;

    if (
      address &&
      (this.bookingDetails?.status === this.helper.BookingStatus.STARTED ||
        this.bookingDetails?.status === this.helper.BookingStatus.ARRIVED_AT_STOP ||
        this.bookingDetails?.status === this.helper.BookingStatus.ARRIVED_AT_PICKUP ||
        this.bookingDetails?.status === this.helper.BookingStatus.SERVICE_COMPLETED)
    ) {
      this.completeBookingDetailsForm.patchValue({
        completeAddress: address.address,
        stopAddress: {
          address: address.address,
          latitude: address.latitude,
          longitude: address.longitude,
          city: address.city,
          countryCode: address.countryCode,
          country: address.country,
          postalCode: address.postalCode,
          note: "",
          placeId: address.placeId
        }
      });
    }
  }

  getAdminVehicleAccessibilityList(): Promise<void> {
    return new Promise((resolve) => {
      const parameters: any = {};
      this.accessibilityService.getAccessibilityList(parameters).subscribe((response: any) => {
        this.customPrices = response?.customPrices ?? [];
        this.setAdditionalPriceString();
        resolve();
      });
    });
  }

  /**
   * Sets the title for additional prices based on the custom prices.
   */
  setAdditionalPriceString(): void {
    this.bookingDetaisAdditionalPrices.forEach((price: any) => {
      for (const cp of this.customPrices) {
        if (price.chargeId === cp._id) {
          price.title = cp.title ?? this.helper.translateService.instant("label-title.additional-charge");
          break; // Stop checking once a match is found
        }
      }
    });
  }

  /**
   * Sets validators for the specific reason input based on the selected cancellation reason.
   */
  setSpecificReasonValidators(): void {
    if (this.completeBookingDetailsForm.value.cancellationReason === "Other") {
      this.completeBookingDetailsForm.get("specificReason")?.setValidators([Validators.required]);
    } else {
      this.completeBookingDetailsForm.get("specificReason")?.setValidators(null);
    }
    this.completeBookingDetailsForm.get("specificReason")?.updateValueAndValidity();
  }
}
